import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { orgApi, schedulingApi } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { actionsStyle, cancelButton, errorBanner, formStyle, generatePassword, hintStyle, inputStyle, labelStyle, pageStyle, primaryButton } from '../formUi';

const EmployeesPage: React.FC<{ unassignedOnly?: boolean; storeId?: string }> = ({ unassignedOnly, storeId }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<any>({ temporaryPassword: '', role: 'employee' });

  const load = async () => {
    try {
      setError(null);
      const params: any = {};
      if (user?.role === 'Managers') params.createdBy = user.id;
      if (storeId) params.storeId = storeId;
      const [emps, storeList] = await Promise.all([
        orgApi.listEmployees(params),
        user?.role === 'Managers' ? orgApi.myStores() : orgApi.listStores(),
      ]);
      setItems(unassignedOnly ? emps.filter((e: any) => !e.managerId) : emps);
      setStores(storeList);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, storeId, unassignedOnly]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await orgApi.createEmployee({
        ...form,
        storeId: form.storeId || stores[0]?.id,
        temporaryPassword: form.temporaryPassword,
        loginUrl: window.location.origin,
      });
      setCreating(false);
      setForm({ temporaryPassword: '', role: 'employee' });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const savePhone = async (id: string, phoneNumber: string) => {
    await orgApi.updateEmployee(id, { phoneNumber });
    await load();
  };

  if (creating) {
    return (
      <div style={pageStyle(600)}>
        <h2>Create New Employee</h2>
        <form onSubmit={create} style={formStyle}>
          {error && <div style={errorBanner}>{error}</div>}
          <div>
            <label style={labelStyle}>Full Name <span style={{ color: '#d32f2f' }}>*</span></label>
            <input required name="name" value={form.name || ''} placeholder="John Doe" onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Email Address <span style={{ color: '#d32f2f' }}>*</span></label>
            <input required type="email" value={form.email || ''} placeholder="employee@company.com" onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Department</label>
            <input value={form.department || ''} placeholder="Engineering, Sales, Marketing, etc." onChange={(e) => setForm({ ...form, department: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>WhatsApp Phone Number</label>
            <input type="tel" value={form.phoneNumber || ''} placeholder="14155552671 (country code + number)" onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} style={inputStyle} />
            <div style={hintStyle}>Optional. Digits with country code — used for manager WhatsApp messaging via Wati.</div>
          </div>
          {stores.length > 1 && (
            <div>
              <label style={labelStyle}>Store <span style={{ color: '#d32f2f' }}>*</span></label>
              <select required value={form.storeId || ''} onChange={(e) => setForm({ ...form, storeId: e.target.value })} style={{ ...inputStyle, background: 'white' }}>
                <option value="">Select a store</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={labelStyle}>Role <span style={{ color: '#d32f2f' }}>*</span></label>
            <select value={form.role || 'employee'} onChange={(e) => setForm({ ...form, role: e.target.value })} style={{ ...inputStyle, background: 'white' }}>
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Temporary Password <span style={{ color: '#d32f2f' }}>*</span></label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input required value={form.temporaryPassword || ''} placeholder="Enter temporary password" onChange={(e) => setForm({ ...form, temporaryPassword: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
              <button type="button" onClick={() => setForm({ ...form, temporaryPassword: generatePassword() })} style={{ padding: '0.75rem 1rem', background: '#f5f5f5', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Generate
              </button>
            </div>
            <small style={{ color: '#666', fontSize: '0.8rem' }}>Password must contain uppercase, lowercase, number, and special character (min 8 chars)</small>
          </div>
          <div style={actionsStyle}>
            <button type="button" onClick={() => { setCreating(false); setError(null); }} style={cancelButton}>Cancel</button>
            <button type="submit" disabled={submitting} style={primaryButton(submitting)}>{submitting ? 'Creating Employee...' : 'Create Employee'}</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <Box>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0 }}>Employees ({items.length})</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={() => setCreating(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            + Create Employee
          </button>
          <button type="button" onClick={load} style={{ padding: '0.5rem 1rem', backgroundColor: '#f5f5f5', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
            🔄 Refresh
          </button>
        </div>
      </div>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {items.length === 0 && <Typography>No employees found. Employees will appear here once they are added to the system.</Typography>}
      <div style={{ display: 'grid', gap: '1rem' }}>
      {items.map((e) => (
        <div key={e.id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: '1.5rem', background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0, color: '#1976d2' }}>{e.name}</h4>
            {e.isActive === false && <Chip size="small" label="INACTIVE" color="error" />}
            <Chip size="small" label={e.schedulingEligible ? 'SCHEDULING ELIGIBLE' : 'NOT ELIGIBLE FOR SCHEDULING'} color={e.schedulingEligible ? 'success' : 'warning'} />
          </div>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', color: '#666' }}>
            <span><strong>Email:</strong> {e.email}</span>
            <span><strong>WhatsApp:</strong> {e.phoneNumber ? `+${String(e.phoneNumber).replace(/\D/g, '')}` : '—'}</span>
            {e.department && <span><strong>Department:</strong> {e.department}</span>}
            {e.manager?.name && <span><strong>Manager:</strong> {e.manager.name}</span>}
          </div>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" onClick={async () => {
              const phone = window.prompt('WhatsApp number (country code, digits)', e.phoneNumber || '');
              if (phone != null) await savePhone(e.id, phone);
            }}>Set phone</Button>
            <Button size="small" onClick={async () => {
              try {
                const r = await schedulingApi.checkEligibility(e.id);
                alert(JSON.stringify(r.result, null, 2));
                await load();
              } catch (err: any) {
                setError(err?.response?.data?.error || err.message);
              }
            }}>Check scheduling</Button>
          </Stack>
        </div>
      ))}
      </div>

    </Box>
  );
};

export default EmployeesPage;
